import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import {
  bindXandarDevice,
  fetchXandarDevices,
  unbindXandarDevice,
} from "../../api/xandarKardianService";

/**
 * react-app #583 — assign a Xandar sensor to a client, with the install
 * location the backend requires.
 *
 * Mirrors the PontosenseStaffSection staff pane: serial in, bind, list what is
 * bound, remove. The one real difference is `installLocation`, which cortex-be
 * has required on bind since 2026-08-25 (XandarKardianDevicesController
 * `bindClientXandarDevice`) — a bind without it is a 400.
 *
 * Why the location matters enough to be mandatory: XK reports
 * `region_presence=1` for "in the primary ROI" and labels it "bed" in their
 * portal even when the ROI is a desk. Caregiver presence copy ("At desk" vs
 * "In bed") follows OUR enum, not the vendor's bed metaphor. The two sensors
 * bound by hand before this UI existed both have a blank location, and that
 * blank sent an overnight-silence investigation down a "sleeping client"
 * theory for four passes when the sensor was in an office (cortex-be#1156).
 */

/**
 * The four values cortex-be accepts, in the order it lists them
 * (lib/xandarInstallLocation.js). This is a closed set on the server, which is
 * why the UI offers a select and never a free-text field.
 */
export const XANDAR_INSTALL_LOCATIONS = Object.freeze([
  { value: "bedroom", label: "Bedroom" },
  { value: "office", label: "Office / desk" },
  { value: "living_room", label: "Living room" },
  { value: "other", label: "Other" },
]);

const LOCATION_LABELS = new Map(
  XANDAR_INSTALL_LOCATIONS.map((o) => [o.value, o.label]),
);

/** Plain words for a stored location, including the pre-#583 blank rows. */
export const installLocationLabel = (value) =>
  LOCATION_LABELS.get(value) || "Not set";

/**
 * Turn a bind/edit failure into caregiver-readable copy.
 *
 * Classify on `err.status`, never on the server's prose — the message is
 * arbitrary backend text and is not a stable contract. The 409 case gets our
 * own wording deliberately: the server's message must not name the other
 * client (an admin asking about client A should not learn who holds the
 * sensor), and ours keeps it that way even if the server's ever changes.
 *
 * A 400 IS passed through, and ONLY a 400: those are a documented contract
 * written for a human ("Enable Xandar Kardian for this client before linking a
 * sensor") and name something the admin must act on. Every other status falls
 * back to generic copy, because `parseJsonOrThrow` derives `message` from the
 * response body — a 5xx can carry a driver error, a stack, or an HTML error
 * page, none of which belong in front of a caregiver admin. This is the same
 * call already made for the list-load failure.
 */
export const describeXandarBindError = (err) => {
  if (err?.status === 409) {
    return "That serial is already assigned to another client. Check the serial, or remove it from the other client first.";
  }
  if (err?.status === 400 && err?.message) return err.message;
  return "Could not assign the sensor. Try again.";
};

/** The same status policy for the remove path. */
export const describeXandarRemoveError = (err) => {
  if (err?.status === 400 && err?.message) return err.message;
  return "Could not remove the sensor. Try again.";
};

function XandarDeviceBindPanelForClient({
  clientId,
  canManage,
  integrationEnabled,
  integrationBusy,
  onBusyChange,
}) {
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);
  const [sensorSn, setSensorSn] = useState("");
  const [installLocation, setInstallLocation] = useState("");
  const [binding, setBinding] = useState(false);
  // Per-sensor edit state, keyed by serial, so editing one row cannot move
  // another row's select.
  const [locationEdits, setLocationEdits] = useState({});
  const [savingSn, setSavingSn] = useState(null);
  const [removingSn, setRemovingSn] = useState(null);
  const [status, setStatus] = useState("");
  const [statusTone, setStatusTone] = useState("info");

  // A bind/remove can outlive the pane: the profile page swaps clients and the
  // section unmounts while a request is still open.
  const isMounted = useRef(true);
  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  // This instance exists for exactly one client — the default export remounts
  // it when the id changes — so no state here can ever belong to another
  // client, and `isMounted` alone drops anything still in flight.
  //
  // Same-client reads can still finish out of order: the assign controls stay
  // usable while the first GET is in flight, so a bind's refresh can land
  // before that GET and then be overwritten by it — the new sensor appears and
  // vanishes. Only the newest read is allowed to commit.
  const listGen = useRef(0);

  const setPanelStatus = (message, tone = "info") => {
    setStatus(message);
    setStatusTone(tone);
  };

  const refreshDevices = useCallback(async () => {
    if (!clientId || !canManage) return;
    const gen = ++listGen.current;
    const isCurrentRead = () => isMounted.current && gen === listGen.current;
    setLoading(true);
    try {
      const data = await fetchXandarDevices(clientId);
      if (!isCurrentRead()) return;
      setDevices(data?.devices || []);
      setLoadFailed(false);
    } catch (_err) {
      if (!isCurrentRead()) return;
      // The raw reason (a socket error, an HTML error page) means nothing to an
      // admin and can carry internals, so it is deliberately not rendered.
      setLoadFailed(true);
    } finally {
      if (isCurrentRead()) setLoading(false);
    }
  }, [clientId, canManage]);

  useEffect(() => {
    refreshDevices();
  }, [refreshDevices]);

  // Only sensors still bound are shown; the backend soft-unbinds by setting
  // status:inactive rather than deleting the row.
  const activeDevices = devices.filter((d) => d?.status !== "inactive");

  const busy = binding || savingSn !== null || removingSn !== null;

  // The integration toggle lives in the parent section and writes the same
  // client. Serialize the two: while a toggle is in flight these controls are
  // inert, and while a device request is in flight the parent disables its
  // switch. Otherwise a disable that lands first turns an in-flight bind into
  // a 400 and unmounts this pane before it can report it — the admin is left
  // with no idea what happened. PontosenseStaffSection serializes the same way.
  const blocked = busy || Boolean(integrationBusy);

  useEffect(() => {
    onBusyChange?.(busy);
  }, [busy, onBusyChange]);

  const handleBind = async () => {
    if (blocked) return;
    const boundSn = sensorSn.trim();
    if (!boundSn) {
      const message = "Enter the Xandar sensor serial number to assign.";
      setPanelStatus(message, "error");
      toast.error(message);
      return;
    }
    // The submit button is disabled without a location; this covers keyboard
    // and programmatic activation, so the server's 400 stays unreachable from
    // a healthy UI.
    if (!installLocation) {
      const message = "Choose where the sensor is installed before assigning.";
      setPanelStatus(message, "error");
      toast.error(message);
      return;
    }
    const isCurrent = () => isMounted.current;
    setBinding(true);
    try {
      await bindXandarDevice(clientId, {
        sensorSn: boundSn,
        installLocation,
      });
      if (!isCurrent()) return;
      setSensorSn("");
      setInstallLocation("");
      await refreshDevices();
      if (!isCurrent()) return;
      // The label is a VALUE, not a noun to drop into a sentence: "installed
      // in the other" and "installed in the office / desk" both read as broken.
      const message = `Sensor ${boundSn} is now assigned to this client. Install location: ${installLocationLabel(
        installLocation,
      )}. Vital signs will appear here once it reports.`;
      setPanelStatus(message, "success");
      toast.success(message);
    } catch (err) {
      if (!isCurrent()) return;
      const message = describeXandarBindError(err);
      setPanelStatus(message, "error");
      toast.error(message);
    } finally {
      if (isCurrent()) setBinding(false);
    }
  };

  /**
   * Editing the location is the same POST as the first bind: a same-client
   * re-POST refreshes installLocation server-side. There is no PATCH.
   */
  const handleSaveLocation = async (sn) => {
    if (blocked || !sn) return;
    const next = locationEdits[sn];
    if (!next) return;
    const isCurrent = () => isMounted.current;
    setSavingSn(sn);
    try {
      await bindXandarDevice(clientId, {
        sensorSn: sn,
        installLocation: next,
      });
      if (!isCurrent()) return;
      await refreshDevices();
      if (!isCurrent()) return;
      setLocationEdits((prev) => {
        const rest = { ...prev };
        delete rest[sn];
        return rest;
      });
      const message = `Sensor ${sn} updated. Install location: ${installLocationLabel(
        next,
      )}.`;
      setPanelStatus(message, "success");
      toast.success(message);
    } catch (err) {
      if (!isCurrent()) return;
      const message = describeXandarBindError(err);
      setPanelStatus(message, "error");
      toast.error(message);
    } finally {
      if (isCurrent()) setSavingSn(null);
    }
  };

  const handleRemove = async (sn) => {
    if (blocked || !sn) return;
    // Removing stops that sensor's vitals routing, so it is confirmed first.
    if (
      typeof window !== "undefined" &&
      typeof window.confirm === "function" &&
      !window.confirm(
        `Remove sensor ${sn} from this client? Its vital signs will stop appearing here.`,
      )
    ) {
      return;
    }
    const isCurrent = () => isMounted.current;
    setRemovingSn(sn);
    try {
      await unbindXandarDevice(clientId, sn);
      if (!isCurrent()) return;
      await refreshDevices();
      if (!isCurrent()) return;
      // Drop any unsaved edit for this row. Keeping it would apply a location
      // the server never stored if the same serial is reassigned later.
      setLocationEdits((prev) => {
        if (!(sn in prev)) return prev;
        const rest = { ...prev };
        delete rest[sn];
        return rest;
      });
      const message = `Sensor ${sn} removed from this client.`;
      setPanelStatus(message, "success");
      toast.success(message);
    } catch (err) {
      if (!isCurrent()) return;
      const message = describeXandarRemoveError(err);
      setPanelStatus(message, "error");
      toast.error(message);
    } finally {
      if (isCurrent()) setRemovingSn(null);
    }
  };

  const cardStyle = {
    backgroundColor: "#fff",
    border: "1px solid #C8E6C9",
    borderRadius: "6px",
    padding: "12px 14px",
    marginBottom: "12px",
  };

  const statusColor =
    statusTone === "success"
      ? "#1B5E20"
      : statusTone === "error"
        ? "#B71C1C"
        : "#1565C0";

  return (
    <div style={cardStyle}>
      <h3 style={{ fontSize: "0.95rem", margin: "0 0 8px", fontWeight: 700 }}>
        Assigned sensor
      </h3>

      {!integrationEnabled ? (
        <p style={{ fontSize: "0.9rem", color: "#555", margin: 0 }}>
          Turn Xandar Kardian on for this client before assigning a sensor.
        </p>
      ) : (
        <>
          <div
            style={{
              display: "flex",
              gap: "8px",
              flexWrap: "wrap",
              alignItems: "center",
              marginBottom: "12px",
            }}
          >
            <label htmlFor="xandar-sensor-sn" style={{ fontSize: "0.85rem" }}>
              Serial number
            </label>
            <input
              id="xandar-sensor-sn"
              name="xandarSensorSn"
              type="text"
              placeholder="Serial from the sensor label"
              value={sensorSn}
              onChange={(e) => setSensorSn(e.target.value)}
              style={{ flex: 1, minWidth: "180px", padding: "8px" }}
              disabled={blocked}
            />
            <label
              htmlFor="xandar-install-location"
              style={{ fontSize: "0.85rem" }}
            >
              Install location
            </label>
            <select
              id="xandar-install-location"
              name="xandarInstallLocation"
              value={installLocation}
              onChange={(e) => setInstallLocation(e.target.value)}
              style={{ padding: "8px" }}
              disabled={blocked}
            >
              <option value="">Choose a location…</option>
              {XANDAR_INSTALL_LOCATIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
            <button
              type="button"
              onClick={handleBind}
              disabled={blocked || !installLocation}
            >
              {binding ? "Assigning…" : "Assign sensor"}
            </button>
          </div>

          <p style={{ fontSize: "0.8rem", color: "#666", margin: "0 0 12px" }}>
            The install location decides how presence reads to caregivers — a
            sensor at a desk should not report the client as in bed.
          </p>

          {loading ? (
            <p style={{ fontSize: "0.85rem", color: "#666", margin: 0 }}>
              Loading assigned sensors…
            </p>
          ) : loadFailed ? (
            <p
              role="alert"
              style={{ fontSize: "0.85rem", color: "#B71C1C", margin: 0 }}
            >
              Could not load the assigned sensors. Refresh the page to try
              again.
            </p>
          ) : activeDevices.length === 0 ? (
            <p style={{ fontSize: "0.85rem", color: "#666", margin: 0 }}>
              No Xandar sensor assigned to this client yet.
            </p>
          ) : (
            <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
              {activeDevices.map((d) => (
                <li
                  key={d.sensorSn}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    flexWrap: "wrap",
                    padding: "6px 0",
                    borderTop: "1px solid #EEE",
                  }}
                >
                  <span style={{ fontFamily: "monospace", fontSize: "0.85rem" }}>
                    {d.sensorSn}
                  </span>
                  <span style={{ fontSize: "0.85rem", color: "#555" }}>
                    {`Installed in: ${installLocationLabel(d.installLocation)}`}
                  </span>
                  <label
                    htmlFor={`xandar-location-${d.sensorSn}`}
                    style={{
                      position: "absolute",
                      left: "-9999px",
                      width: "1px",
                      height: "1px",
                      overflow: "hidden",
                    }}
                  >
                    {`Install location for ${d.sensorSn}`}
                  </label>
                  <select
                    id={`xandar-location-${d.sensorSn}`}
                    value={locationEdits[d.sensorSn] ?? d.installLocation ?? ""}
                    onChange={(e) =>
                      setLocationEdits((prev) => ({
                        ...prev,
                        [d.sensorSn]: e.target.value,
                      }))
                    }
                    style={{ padding: "4px" }}
                    disabled={blocked}
                  >
                    <option value="">Choose a location…</option>
                    {XANDAR_INSTALL_LOCATIONS.map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                  <button
                    type="button"
                    aria-label={`Save location for ${d.sensorSn}`}
                    onClick={() => handleSaveLocation(d.sensorSn)}
                    disabled={blocked || !locationEdits[d.sensorSn]}
                  >
                    {savingSn === d.sensorSn ? "Saving…" : "Save location"}
                  </button>
                  <button
                    type="button"
                    aria-label={`Remove sensor ${d.sensorSn}`}
                    onClick={() => handleRemove(d.sensorSn)}
                    disabled={blocked}
                    style={{ marginLeft: "auto" }}
                  >
                    {removingSn === d.sensorSn ? "Removing…" : "Remove"}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </>
      )}

      {status ? (
        <p
          style={{
            fontSize: "0.85rem",
            color: statusColor,
            margin: "10px 0 0",
          }}
          role={statusTone === "error" ? "alert" : "status"}
          aria-live={statusTone === "error" ? "assertive" : "polite"}
        >
          {status}
        </p>
      ) : null}
    </div>
  );
}

/**
 * Binding is admin-only server-side (`assertAdminWrite`), and a caregiver has
 * no action to take here.
 *
 * The `key` is the load-bearing part: without it, a `clientId` change with no
 * remount renders the PREVIOUS client's devices, serial, edits and status
 * once, and an effect can only clear them AFTER that render commits — one
 * painted frame showing client A's serial on client B's profile. That is a
 * disclosure, not a flicker. Remounting scopes every piece of state to the
 * client it was read for, by construction, rather than depending on a `key`
 * in the parent file or on cleanup that runs too late.
 */
export default function XandarDeviceBindPanel({
  clientId,
  canManage = false,
  integrationEnabled = false,
  integrationBusy = false,
  onBusyChange,
}) {
  if (!canManage) return null;
  return (
    <XandarDeviceBindPanelForClient
      key={clientId}
      clientId={clientId}
      canManage={canManage}
      integrationEnabled={integrationEnabled}
      integrationBusy={integrationBusy}
      onBusyChange={onBusyChange}
    />
  );
}
